import { Layer } from 'grommet';
import { ReactNode, useEffect, useState } from 'preact/compat';

interface SpeedyFlowShellProps {
  children: ReactNode;
  onDismiss: () => void;
  /**
   * What this step is, for assistive technology. Announced on change: the modal swaps its contents
   * in place rather than navigating, so without this a screen reader user hears nothing and may
   * think it closed.
   */
  stepLabel?: string;
  /**
   * A centred popup on both viewports rather than a bottom sheet on mobile. The completion step
   * uses this: it is a confirmation, not a task, so it should not swallow the page it is confirming.
   */
  popupOnMobile?: boolean;
  /**
   * Whether Escape and a backdrop click dismiss the flow. Declared by the trigger site: only it
   * knows whether the page behind is usable without the modal. The restricted habitat page is not —
   * it is a blurred placeholder — so dismissing there would strand the visitor on an empty page.
   *
   * The browser back button still closes the flow, so this is not a dead end.
   */
  persistent?: boolean;
}

/**
 * The modal frame: a bottom sheet on mobile, a centred popup on desktop, scrollable in both because
 * several steps overflow a short viewport.
 *
 * The mobile/desktop split is read from the body class the layout sets, and the `key` forces a
 * remount on change because grommet's Layer cannot switch between full and positioned in place.
 */
const SpeedyFlowShell = ({
  children,
  onDismiss,
  stepLabel,
  popupOnMobile = false,
  persistent = false,
}: SpeedyFlowShellProps) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const read = () => setIsMobile(document.body.classList.contains('group/mobile-layout'));
    const observer = new MutationObserver(read);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    read();
    return () => observer.disconnect();
  }, []);

  const sheet = isMobile && !popupOnMobile;

  return (
    <Layer
      key={`${isMobile ? 'mobile' : 'desktop'}-${popupOnMobile ? 'popup' : 'full'}`}
      full={sheet ? 'horizontal' : false}
      animation={isMobile ? 'slide' : 'fadeIn'}
      position={sheet ? 'bottom' : 'center'}
      className={
        sheet
          ? // `max-w-full` is not redundant: the app's grommet theme gives every layer a
            // `calc(100vw - 30px)` container, which is the right gutter for an ordinary dialog and
            // the wrong thing entirely for one that spans the width of the screen — it left a 30px
            // strip of the page showing down one side. The desktop branch below overrides the same
            // rule with its own width, which is why only mobile showed it.
            //
            // The height leaves a strip of the page showing above the sheet, so it reads as
            // covering the page rather than replacing it. 64px is the strip we want, but on a short
            // viewport that is a large share of the screen, so the 90% floor takes over there and
            // gives the sheet its height back.
            //
            // `svh` rather than `vh` so the sheet is measured against the viewport the mobile
            // browser shows with its chrome expanded; `vh` would put the bottom of the sheet under
            // the address bar until the user scrolls.
            '!max-h-[max(90svh,calc(100svh_-_64px))] !w-full !max-w-full !overflow-y-auto !rounded-t-2xl'
          : '!max-h-[calc(100vh-3rem)] !w-[calc(100%-3rem)] !max-w-[406px] !overflow-y-auto !rounded-2xl'
      }
      /*
       * Always handlers, never `undefined`, with the decision inside them.
       *
       * grommet wraps its content in a `Keyboard` only when `onEsc` is truthy, so letting these go
       * undefined changes the element tree above the children and remounts the entire flow — every
       * step's state with it. That is invisible while `persistent` is fixed for a flow's lifetime,
       * and destructive the moment it changes mid-flow: a step that opens a modal of its own (see
       * `useCoveringModal`) would have its own children torn down as the modal opened, and the
       * modal would never appear.
       */
      onEsc={() => {
        if (!persistent) onDismiss();
      }}
      onClickOutside={() => {
        if (!persistent) onDismiss();
      }}
    >
      <div
        className="flex min-h-full flex-col"
        data-component="speedy-flow"
        data-persistent={persistent ? 'true' : undefined}
        role="dialog"
        aria-modal="true"
        aria-label={stepLabel}
      >
        <span className="sr-only" aria-live="polite">
          {stepLabel}
        </span>
        {children}
      </div>
    </Layer>
  );
};

export default SpeedyFlowShell;
